import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogForm,
  DialogHeading,
  FilterButton,
  FormCheckbox,
  FormCheckboxGroup,
  Popover,
} from 'omakase';
import { Container } from 'ui';
import { Form, Formik } from 'formik';
import { Theme, css } from '@emotion/react';
import { consumer } from '@tock/coretypes';
import { popoverPaperRoot } from 'components/www/availability-search/searchBarAndButtonCssDefs';
import { useCuisineAggregations } from 'hooks/search/useCuisineAggregations';
import { useCuisineFilters } from 'hooks/search/useCuisineFilters';

interface Props {
  selectedCuisines: string[];
  onSubmit(cuisine: string[]): void;
  isOpen: boolean;
  anchorEl?: HTMLButtonElement;
  onOpen(e: React.MouseEvent<HTMLButtonElement>): void;
  onClose(): void;
  isMobile?: boolean;
  aggregations?: consumer.Aggregations;
}

interface Fields {
  selectedCuisines: string[];
}

export const PLACEHOLDER_CUISINES = [
  'American',
  'Sushi',
  // 'Chinese',
  'Asian',
  'Italian',
  'Winery',
  'French',
  // todo fix ampersand filters in elasticsearch
  // 'Breakfast & Brunch',
  // 'Coffee & Tea',
  // 'Café & Bakery',
  'Barbecue',
  'Mexican',
  'Tacos',
  // 'Tapas & Small Plates',
  // 'Latin American',
  'Spanish',
  // 'Burgers',
  // 'Sandwiches',
  'Pizza',
  'Indian',
  'Bar',
  // 'Brewery & Distillery',
  'Japanese',
  'Seafood',
  // 'Salads',
  // 'Vegetarian & Vegan',
  // 'Dessert',
  'Steakhouse',
  'European',
  'Mediterranean',
  // 'Middle Eastern',
  'Wine Shop',
  'British',
  'Canadian',
  'Pasta',
  // 'African',
  'Caribbean',
  'International',
];

export const PLACEHOLDER_CUISINES_SET = new Set(
  PLACEHOLDER_CUISINES.map((cuisine) => cuisine.toLowerCase())
);

const container = ({ spacing }: Theme) => css`
  padding: ${spacing(2.75)}px ${spacing(3.5)}px ${spacing(3.25)}px;
`;

const popoverBody = ({ spacing }: Theme) => css`
  width: 448px;
`;

const submitButtonContainer = ({ spacing, colors }: Theme) => css`
  display: flex;
  justify-content: flex-end;
  margin-top: ${spacing(2)}px;
  padding-top: ${spacing(2.25)}px;
  padding-bottom: ${spacing(2.25)}px;
  border-top: 1px solid ${colors.borderDefault};
`;

const DIALOG_TITLE = 'cusines-dialog-title';

const CuisineFilterButton: React.FunctionComponent<Props> = ({
  selectedCuisines,
  onSubmit,
  isOpen,
  onClose,
  onOpen,
  anchorEl,
  isMobile,
  aggregations,
}) => {
  const { sendCuisineGAEvents } = useCuisineFilters();
  const { cuisines } = useCuisineAggregations(selectedCuisines, aggregations);

  const handleSubmit = (values: Fields) => {
    sendCuisineGAEvents(values.selectedCuisines);
    onSubmit(values.selectedCuisines);
    onClose();
  };

  const numberCuisinesSelected = selectedCuisines.length;

  const buttonLabel =
    numberCuisinesSelected > 1
      ? `Cuisines · ${numberCuisinesSelected}`
      : numberCuisinesSelected === 1
        ? selectedCuisines[0]
        : 'Cuisines';

  const ariaButtonLabel =
    numberCuisinesSelected === 0
      ? 'Edit cuisine type filter'
      : numberCuisinesSelected === 1
        ? `Cuisine type filter: ${selectedCuisines[0]}`
        : `Cuisine type filter: ${numberCuisinesSelected} selected`;

  const hideButton = cuisines.length < 10 && numberCuisinesSelected === 0;

  if (hideButton) {
    return null;
  }

  return (
    <>
      <FilterButton
        aria-haspopup
        aria-label={ariaButtonLabel}
        onClick={onOpen}
        selected={numberCuisinesSelected > 0}
      >
        {buttonLabel}
      </FilterButton>
      {isMobile && (
        <Dialog aria-labelledby={DIALOG_TITLE} variant="partial" open={isOpen} onClose={onClose}>
          <Formik<Fields> initialValues={{ selectedCuisines }} onSubmit={handleSubmit}>
            <DialogForm>
              <DialogHeading id={DIALOG_TITLE} onClose={onClose}>
                Cuisines
              </DialogHeading>
              <DialogContent>
                <FormCheckboxGroup
                  label="Cuisines"
                  hideLabel
                  disclosureMode="static"
                  name="selectedCuisines"
                >
                  {cuisines.map((cuisineOption) => (
                    <FormCheckbox
                      key={`${cuisineOption}-mobile`}
                      id={cuisineOption}
                      name="selectedCuisines"
                      label={cuisineOption}
                      value={cuisineOption}
                      size="large"
                      weight="book"
                    />
                  ))}
                </FormCheckboxGroup>
              </DialogContent>
              <DialogActions>
                <Button size="large" variant="primary" type="submit">
                  Submit
                </Button>
              </DialogActions>
            </DialogForm>
          </Formik>
        </Dialog>
      )}

      {!isMobile && (
        <Popover
          css={popoverPaperRoot}
          anchorEl={anchorEl}
          open={isOpen}
          onClose={onClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <div css={popoverBody}>
            <Formik<Fields> initialValues={{ selectedCuisines }} onSubmit={handleSubmit}>
              <Form>
                <Container css={container}>
                  <FormCheckboxGroup
                    label="Cuisines"
                    disclosureMode="static"
                    name="selectedCuisines"
                    hideLabel
                    smColumns={2}
                  >
                    {cuisines.map((cuisineOption) => (
                      <FormCheckbox
                        key={`${cuisineOption}-desktop`}
                        id={cuisineOption}
                        name="selectedCuisines"
                        label={cuisineOption}
                        value={cuisineOption}
                        size="large"
                        weight="book"
                      />
                    ))}
                  </FormCheckboxGroup>
                </Container>
                <Container css={submitButtonContainer}>
                  <Button variant="primary" size="large" type="submit">
                    Submit
                  </Button>
                </Container>
              </Form>
            </Formik>
          </div>
        </Popover>
      )}
    </>
  );
};

export default CuisineFilterButton;
